import { useEffect, useState } from 'react';
import { Download, ExternalLink, Loader2, FileText, X } from 'lucide-react';
import { useApiUrl } from '@refinedev/core';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PaymentDocument } from './PaymentDocumentsUpload';

interface DocumentPreviewDialogProps {
  document: PaymentDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Универсальная модалка для просмотра и скачивания документов платежа.
 * Изображения рендерятся напрямую, PDF — в <iframe>. Контент PRIVATE-файлов
 * подгружается blob'ом с JWT-хедером, поэтому iframe/img получают object URL.
 */
export function DocumentPreviewDialog({ document: doc, open, onOpenChange }: DocumentPreviewDialogProps) {
  const apiUrl = useApiUrl();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc || !open) {
      return;
    }

    let revokedUrl: string | null = null;
    let cancelled = false;

    async function load() {
      if (!doc) { return; }
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('accessToken');
        // doc.url относительный (например /api/files/8/stream) — берём его как есть.
        // Для абсолютных URL fetch тоже справится.
        const res = await fetch(doc.url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          throw new Error(`Не удалось загрузить: ${res.status}`);
        }
        const blob = await res.blob();
        if (cancelled) { return; }
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Ошибка загрузки');
        }
      } finally {
        if (!cancelled) { setIsLoading(false); }
      }
    }

    load();

    return () => {
      cancelled = true;
      if (revokedUrl) { URL.revokeObjectURL(revokedUrl); }
      setBlobUrl(null);
    };
    // apiUrl добавлен для корректного реинита если он когда-то поменяется
  }, [doc, open, apiUrl]);

  const handleDownload = async () => {
    if (!doc) { return; }
    try {
      // Если blob уже загружен — переиспользуем; иначе загружаем отдельно
      let url = blobUrl;
      let createdHere = false;
      if (!url) {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(doc.url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          throw new Error(`Не удалось скачать: ${res.status}`);
        }
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        createdHere = true;
      }

      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.originalName;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      if (createdHere) { URL.revokeObjectURL(url); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка скачивания');
    }
  };

  const handleOpenInNewTab = () => {
    if (!blobUrl) { return; }
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  };

  if (!doc) { return null; }

  const isImage = doc.mimeType.startsWith('image/');
  const isPdf = doc.mimeType === 'application/pdf';

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) { return `${bytes} Б`; }
    if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} КБ`; }
    return `${(bytes / 1024 / 1024).toFixed(2)} МБ`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">{doc.originalName}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            {doc.mimeType} · {formatSize(doc.size)}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto rounded border bg-muted/30">
          {isLoading && (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Загружаю документ…
            </div>
          )}

          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive px-4 text-center">
              <X className="h-8 w-8" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          {!isLoading && !error && blobUrl && isImage && (
            <div className="flex items-center justify-center p-4">
              <img
                src={blobUrl}
                alt={doc.originalName}
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            </div>
          )}

          {!isLoading && !error && blobUrl && isPdf && (
            <iframe
              src={blobUrl}
              title={doc.originalName}
              sandbox="allow-same-origin"
              className="w-full h-[70vh] border-0 bg-white"
            />
          )}

          {!isLoading && !error && blobUrl && !isImage && !isPdf && (
            <div className="flex flex-col items-center justify-center h-64 gap-2 text-muted-foreground px-4 text-center">
              <FileText className="h-8 w-8" />
              <div className="text-sm">Предпросмотр недоступен для {doc.mimeType}</div>
              <div className="text-xs">Нажмите «Скачать» чтобы сохранить файл</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          {blobUrl && (isImage || isPdf) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenInNewTab}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              В новой вкладке
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={handleDownload}
            disabled={isLoading}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Скачать
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
